import React, { useEffect, useRef, useState } from "react";
import { Loader2, Save, UserCheck, Trash2, Edit, Plus, RefreshCw, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import axios from "../../api/axios";

import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

import {
  masterFormCard,
  masterFormFieldGroup,
  masterInputLabel,
  masterInput,
  masterSelect,
  masterFormBtnSubmit,
  masterBtnBack,
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBadgeLive,
  masterBadgeInactive,
  masterActionBtnEdit,
  masterActionBtnDelete,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const FacultyMaster = () => {
  const fileInputRef = useRef(null);

  const [viewMode, setViewMode] = useState("form");
  const [faculties, setFaculties] = useState([]);
  const [editId, setEditId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    teacherName: "",
    subject: "",
    designation: "FACULTY",
    description: "",
    joiningDate: "",
    terminateDate: "",
    photo: null,
    status: "ACTIVE",
  });

  const [currentPhotoName, setCurrentPhotoName] = useState("");

  const fetchFaculties = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/master/faculty/all");
      setFaculties(res.data?.data || []);
    } catch {
      toast.error("FAILED TO LOAD FACULTY DATABASE");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaculties();
  }, []);

  const resetForm = () => {
    setEditId(null);
    setCurrentPhotoName("");
    setFormData({
      teacherName: "",
      subject: "",
      designation: "FACULTY",
      description: "",
      joiningDate: "",
      terminateDate: "",
      photo: null,
      status: "ACTIVE",
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.teacherName.trim()) return toast.error("TEACHER NAME IS REQUIRED");
    if (!formData.subject.trim()) return toast.error("SUBJECT IS REQUIRED");
    if (!formData.designation.trim()) return toast.error("DESIGNATION IS REQUIRED");

    setIsSaving(true);
    try {
      let base64Photo = null;
      let photoName = null;

      if (formData.photo) {
        base64Photo = await convertFileToBase64(formData.photo);
        photoName = formData.photo.name;
      }

      const payload = {
        id: editId || undefined,
        teacherName: formData.teacherName.toUpperCase(),
        subject: formData.subject.toUpperCase(),
        designation: formData.designation.toUpperCase(),
        description: formData.description,
        joiningDate: formData.joiningDate, // Add this line
        terminateDate: formData.terminateDate,
        status: (formData.status || "ACTIVE").toUpperCase(),
      };

      if (base64Photo) {
        payload.photoPath = base64Photo;
        payload.photoName = photoName;
      }

      const res = await axios.post("/master/faculty/upsert", payload);

      if (res.data.success || res.status >= 200) {
        toast.success(editId ? "FACULTY UPDATED SUCCESSFULLY" : "FACULTY SAVED SUCCESSFULLY");
        fetchFaculties();
        resetForm();
        setViewMode("form"); // Stays on form page & resets completely
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "SAVE FAILED - CHECK SERVER");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditId(item._id);
    setCurrentPhotoName(item.photoName || "");
    setFormData({
      teacherName: item.teacherName || "",
      subject: item.subject || "",
      designation: item.designation || "FACULTY",
      description: item.description || "",
      joiningDate: item.joiningDate || "",
      terminateDate: item.terminateDate || "",
      photo: null,
      status: item.status || "ACTIVE",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
    setViewMode("form");
  };

  const handleDelete = async (recordId) => {
    if (window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS FACULTY?")) {
      try {
        await axios.delete(`/master/faculty/${recordId}`);
        toast.success("DELETED SUCCESSFULLY");
        fetchFaculties();
      } catch {
        toast.error("DELETE FAILED");
      }
    }
  };

  const filteredData = faculties.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.teacherName?.toLowerCase().includes(term) ||
      item.subject?.toLowerCase().includes(term) ||
      item.designation?.toLowerCase().includes(term) ||
      item.facultyId?.toLowerCase().includes(term)
    );
  });

  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar`}>
      {viewMode === "form" ? (
        <div>
          {/* HEADER CARD FOR FORM VIEW */}
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <UserCheck size={13} />
                </div>
                <h1 className={masterHeadingMain}>{editId ? "Edit Faculty" : "Faculty Form"}</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("list");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>VIEW LIST</span>
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className={`${masterFormCard} flex flex-col gap-2 mt-3`}>
            {/* ROW 1: INPUT FIELDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Teacher Name</label>
                <input
                  autoFocus
                  type="text"
                  value={formData.teacherName}
                  onChange={(e) =>
                    setFormData({ ...formData, teacherName: e.target.value.toUpperCase() })
                  }
                  placeholder="ENTER TEACHER NAME"
                  className={masterInput}
                  required
                />
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value.toUpperCase() })
                  }
                  placeholder="ENTER SUBJECT"
                  className={masterInput}
                  required
                />
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Designation</label>
                <select
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  className={masterSelect}
                  required
                >
                  <option value="SENIOR FACULTY">SENIOR FACULTY</option>
                  <option value="FACULTY">FACULTY</option>
                  <option value="ASSISTANT PROFESSOR">ASSISTANT PROFESSOR</option>
                  <option value="PROFESSOR">PROFESSOR</option>
                  <option value="HOD">HOD</option>
                  <option value="GUEST FACULTY">GUEST FACULTY</option>
                </select>
              </div>
           
           
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Joining Date</label>
                <input
                  type="text"
                  value={formData.joiningDate}
                  onChange={(e) => setFormData({ ...formData, joiningDate: e.target.value })}
                  placeholder="DD-MM-YYYY"
                  className={masterInput}
                />
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Terminate Date </label>
                <input
                  type="text"
                  value={formData.terminateDate}
                  onChange={(e) => setFormData({ ...formData, terminateDate: e.target.value })}
                  placeholder="DD-MM-YYYY"
                  className={masterInput}
                />
              </div>
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className={masterSelect}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
              </div>
            

            {/* ROW 2: DESCRIPTION TEXTAREA, PHOTO UPLOAD, STATUS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Description</label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="ENTER DESCRIPTION / BIO"
                  className={`${masterInput} py-1.5 h-auto resize-none`}
                />
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>
                  Upload Photo {currentPhotoName && <span className="text-blue-600 font-normal">({currentPhotoName})</span>}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    setFormData({ ...formData, photo: e.target.files[0] });
                    if (e.target.files[0]) setCurrentPhotoName(e.target.files[0].name);
                  }}
                  className={masterInput}
                />
              </div>

              
            </div>

            {/* ROW 3: BUTTONS (LEFT BACK, RIGHT SAVE) */}
            <div className="flex items-center justify-between w-full pt-1">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setViewMode("list");
                }}
                className={`${masterBtnBack} flex items-center justify-center gap-1.5 h-[26px]`}
              >
                <ArrowLeft size={14} />
                <span>BACK</span>
              </button>

              <div>
                <button
                  disabled={isSaving || loading}
                  type="submit"
                  className={masterFormBtnSubmit}
                >
                  {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {editId ? "UPDATE" : "SAVE"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex flex-col flex-1 h-full">
          {/* HEADER CARD */}
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <UserCheck size={13} />
                </div>
                <h1 className={masterHeadingMain}>Faculty List</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={fetchFaculties}
                  className={masterBtnBack}
                  title="Refresh Records"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  <span>SYNC</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("form");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>ADD NEW</span>
                </button>
              </div>
            </div>
          </div>

          {/* COMBINED SEARCH & PAGINATION BAR */}
          <PaginationSearchbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="SEARCH FACULTY ID, NAME, SUBJECT, OR DESIGNATION..."
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalItems={filteredData.length}
          />

          {/* TABLE */}
          <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col`}>
            <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
              <table className={masterTable}>
                <thead>
                  <tr className={masterTableHead}>
                    <th className={masterThCode}>SL NO</th>
                    <th className={masterThCode}>FACULTY ID</th>
                    <th className={masterThName}>TEACHER NAME</th>
                    <th className={masterThName}>SUBJECT</th>
                    <th className={masterThName}>DESIGNATION</th>
                    <th className={masterThName}>PHOTO</th>
                    <th className={masterThStatus}>STATUS</th>
                    <th className={masterThManage}>MANAGE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className={masterTdEmpty}>
                        Syncing Database...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <tr key={item._id} className={masterTableRow}>
                        <td className={masterTdCode}>{startIndex + index + 1}</td>
                        <td className={masterTdCode}>{item.facultyId}</td>
                        <td className={masterTdName}>{item.teacherName}</td>
                        <td className={masterTdName}>{item.subject}</td>
                        <td className={masterTdName}>{item.designation}</td>
                        <td className={masterTdName}>
                          {item.photoPath ? (
                            <img src={item.photoPath} alt="Faculty" className="h-8 w-8 object-cover rounded-full border border-slate-300" />
                          ) : (
                            "---"
                          )}
                        </td>
                        <td className={masterTdStatus}>
                          <span
                            className={
                              item.status === "INACTIVE"
                                ? masterBadgeInactive
                                : masterBadgeLive
                            }
                          >
                            {item.status || "ACTIVE"}
                          </span>
                        </td>
                        <td className={masterTdManage}>
                          <div className={masterTdManageFlex}>
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className={masterActionBtnEdit}
                              title="Edit"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item._id)}
                              className={masterActionBtnDelete}
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className={masterTdEmpty}>
                        No Faculty Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacultyMaster;